<script>
    import {providerLocale} from '$lib/providerLocale.js';
    import {t} from '$utils/lang.js';
    import { devLog as _devLog } from '$utils/devLog.js';
    import { paymentStore } from '$lib/paymentStore.svelte.js';
    import { onDestroy } from 'svelte';
    const devLog = (tag, ...args) => _devLog(`AmazonPayExpress:${tag}`, ...args);

    let {
        paymentId,
        metadata,
    } = $props();

    let isLoading = $state(true);
    let errorMessage = $state(null);
    let containerId = 'amazon-pay-express-' + Math.random().toString(36).slice(2);
    let initialized = false;

    /**
     * Amazon-Pay-SDK's `renderButton(selector, …)` resolves the selector via
     * `document.querySelector` — kann KEINE Shadow-DOMs durchqueren. Da
     * <m7-checkout> in Production einen Shadow-Root nutzt, müssen wir den
     * SDK-Button-Container im light-DOM (document.body) anlegen. Custom-
     * Button im Shadow-DOM triggert ihn via JS-click; visuell ist der Container
     * eh hidden — kein Layout-Konflikt.
     */
    function ensureLightContainer() {
        let el = document.getElementById(containerId);
        if (el) return el;
        el = document.createElement('div');
        el.id = containerId;
        el.style.display = 'none';
        document.body.appendChild(el);
        return el;
    }

    onDestroy(() => {
        const el = document.getElementById(containerId);
        if (el && el.parentElement === document.body) el.remove();
    });

    function triggerAmazonButton() {
        const container = document.getElementById(containerId);
        // Amazon Pay v2 renders the clickable element inside a shadow root (not a <button>)
        const btn = container?.shadowRoot?.querySelector('[tabindex="0"]')
                 ?? container?.shadowRoot?.querySelector('.amazonpay-button-view1')
                 ?? container?.querySelector('button')
                 ?? container;
        btn?.click();
    }

    $effect(() => {
        const init = async () => {
            if (initialized) return;
            initialized = true;
            try {
                ensureLightContainer();
                const params = new URLSearchParams({
                    cl: 'm7-checkout-dpc',
                    fnc: 'createExpressOrder',
                    payment_id: paymentId,
                });
                const res = await fetch(`/index.php?${params}`, {method: 'POST'});
                if (!res.ok) throw new Error(t('Netzwerkfehler beim Laden der Amazon Pay Konfiguration.'));
                const config = await res.json();
                if (!config?.payloadJSON || !config?.signature) {
                    throw new Error(config?.message ?? t('Amazon Pay Express Konfiguration nicht verfügbar.'));
                }

                await new Promise((resolve, reject) => {
                    if (window.amazon?.Pay) { resolve(undefined); return; }
                    const script = document.createElement('script');
                    script.src = 'https://static-eu.payments-amazon.com/checkout.js';
                    script.onload = resolve;
                    script.onerror = () => reject(new Error(t('Amazon Pay JS SDK konnte nicht geladen werden.')));
                    document.head.appendChild(script);
                });

                const initCheckoutConfig = {
                    merchantId: metadata.merchantId,
                    publicKeyId: metadata.publicKeyId,
                    ledgerCurrency: metadata.ledgerCurrency ?? 'EUR',
                    checkoutLanguage: metadata.checkoutLanguage ?? providerLocale(),
                    productType: 'PayAndShip',
                    placement: metadata.placement ?? 'Cart',
                    sandbox: metadata.sandbox ?? false,
                    alwaysRedirect: true,
                    createCheckoutSessionConfig: {
                        payloadJSON: config.payloadJSON,
                        signature: config.signature,
                        publicKeyId: config.publicKeyId,
                    },
                };

                window.amazon.Pay.renderButton('#' + containerId, initCheckoutConfig);

                // Expose button-click path for test automation (alwaysRedirect=true → no popup needed)
                window._m7AmazonExpressInit = triggerAmazonButton;

                isLoading = false;
            } catch (err) {
                isLoading = false;
                errorMessage = err.message ?? t('Amazon Pay konnte nicht geladen werden. Bitte Seite neu laden.');
                paymentStore.error = errorMessage;
                devLog('init:error', {error: err.message});
            }
        };

        init();
    });
</script>

<!--
    Amazon-Pay-SDK-Button-Container wird zur Laufzeit in document.body
    appended (Shadow-DOM-Bypass für renderButton's document.querySelector).
    Hier kein Platzhalter mehr — siehe ensureLightContainer().
-->

<!-- Custom full-width button matching legacy style -->
{#if !errorMessage}
    <div class="w-full py-[5px]">
        <button
            onclick={triggerAmazonButton}
            disabled={isLoading}
            class="w-full h-[42px] flex items-center justify-center gap-2 rounded border-2 border-[#736952] px-2 cursor-pointer hover:brightness-95 active:brightness-90 transition-[filter] disabled:opacity-50 disabled:cursor-not-allowed"
            style="background: linear-gradient(to bottom, #f9e4b4 10%, #efc14f);"
            type="button"
            data-checkout-action="amazon-pay-express"
        >
            {#if isLoading}
                <div class="h-4 w-24 bg-[#e8b84b] animate-pulse rounded"></div>
            {:else}
                <span class="text-[13px] font-bold text-[#333e47] leading-normal pr-2">{t('Bezahlen über')}</span>
                <svg xmlns="http://www.w3.org/2000/svg" style="isolation:isolate" viewBox="0 0 113.52 33.86" height="24" aria-label="Amazon"><defs><clipPath id="amazon-clip-express"><path d="M0 0h176.51v33.86H0z"/></clipPath></defs><g clip-path="url(#amazon-clip-express)" fill-rule="evenodd"><path d="M69.65 26.49c-6.52 4.81-15.98 7.37-24.12 7.37a43.66 43.66 0 0 1-29.47-11.24c-.61-.55-.07-1.3.67-.88a59.32 59.32 0 0 0 29.48 7.83c7.23 0 15.18-1.5 22.5-4.6 1.1-.47 2.02.72.94 1.52z" fill="#333E47"/><path d="M72.37 23.39c-.84-1.07-5.52-.5-7.62-.25-.64.07-.74-.48-.16-.89 3.73-2.62 9.85-1.86 10.56-.98.72.88-.19 7.02-3.69 9.94-.53.45-1.05.21-.8-.38.78-1.97 2.54-6.37 1.7-7.44z" fill="#333E47"/><path d="M64.9 3.72V1.18c0-.4.3-.65.64-.65h11.42c.37 0 .66.27.66.64v2.19c0 .37-.3.84-.86 1.6l-5.91 8.45c2.2-.05 4.52.28 6.51 1.4.45.25.57.63.6 1v2.71c0 .38-.4.81-.83.59a13.15 13.15 0 0 0-12.07.02c-.4.21-.81-.22-.81-.6v-2.58c0-.41 0-1.12.42-1.75l6.86-9.83h-5.97a.64.64 0 0 1-.66-.65zM23.23 19.64h-3.47a.65.65 0 0 1-.62-.59V1.22c0-.36.3-.64.67-.64h3.24c.33.01.6.27.63.6V3.5h.06C24.6 1.25 26.18.2 28.32.2c2.17 0 3.53 1.05 4.5 3.3a4.93 4.93 0 0 1 4.8-3.3c1.47 0 3.05.6 4.03 1.95 1.1 1.5.87 3.68.87 5.6V19c0 .36-.3.64-.67.64h-3.47a.65.65 0 0 1-.62-.64V9.55c0-.75.06-2.63-.1-3.34-.26-1.2-1.04-1.54-2.04-1.54a2.3 2.3 0 0 0-2.08 1.46c-.36.9-.33 2.4-.33 3.42V19c0 .36-.3.64-.67.64h-3.47a.65.65 0 0 1-.62-.64V9.55c0-2 .32-4.92-2.14-4.92-2.5 0-2.4 2.85-2.4 4.92V19c0 .36-.3.64-.68.64zM87.49 3.84c-2.56 0-2.73 3.5-2.73 5.67 0 2.18-.03 6.83 2.7 6.83 2.69 0 2.82-3.75 2.82-6.04 0-1.5-.07-3.3-.52-4.73-.39-1.24-1.17-1.73-2.27-1.73zM87.46.2c5.15 0 7.94 4.43 7.94 10.06 0 5.44-3.08 9.76-7.94 9.76-5.06 0-7.82-4.43-7.82-9.95 0-5.55 2.79-9.87 7.82-9.87zM102.1 19.64h-3.47A.65.65 0 0 1 98 19L98 1.16a.66.66 0 0 1 .67-.58h3.22c.3.01.55.22.62.5V3.8h.06c.98-2.44 2.34-3.6 4.74-3.6 1.56 0 3.08.56 4.05 2.1.91 1.43.91 3.83.91 5.56v11.22a.66.66 0 0 1-.66.56h-3.49a.65.65 0 0 1-.62-.56V9.4c0-1.96.23-4.8-2.17-4.8-.84 0-1.62.56-2.01 1.42a7.93 7.93 0 0 0-.55 3.38V19c0 .36-.3.64-.68.64zM59.3 19.6a.72.72 0 0 1-.83.08c-1.15-.96-1.36-1.4-1.99-2.32-1.9 1.95-3.26 2.53-5.73 2.53-2.92 0-5.2-1.8-5.2-5.42a5.9 5.9 0 0 1 3.7-5.68c1.9-.82 4.53-.97 6.54-1.2v-.45c0-.83.07-1.8-.42-2.52-.42-.64-1.24-.9-1.95-.9-1.33 0-2.5.67-2.8 2.08-.05.31-.28.63-.6.64l-3.37-.36c-.28-.07-.6-.3-.52-.73C46.9 1.32 50.5.06 53.77.03h.26c1.67.02 3.8.48 5.11 1.73 1.7 1.58 1.53 3.69 1.53 5.98v5.41c0 1.63.67 2.35 1.3 3.22.23.32.28.7 0 .93l-2.67 2.3zm-3.5-8.48c0 1.36.02 2.49-.66 3.7-.55.97-1.43 1.57-2.4 1.57-1.34 0-2.12-1.02-2.12-2.52 0-2.96 2.66-3.5 5.17-3.5v.76zM13.74 19.6a.72.72 0 0 1-.82.08c-1.15-.96-1.36-1.4-1.99-2.32-1.9 1.95-3.26 2.53-5.73 2.53-2.92 0-5.2-1.8-5.2-5.42A5.9 5.9 0 0 1 3.7 8.8c1.9-.82 4.52-.97 6.54-1.2v-.45c0-.83.06-1.8-.42-2.52-.43-.64-1.24-.9-1.95-.9-1.33 0-2.5.67-2.8 2.08-.06.31-.29.63-.6.64L1.1 6.08C.82 6 .5 5.78.58 5.35 1.35 1.32 4.95.06 8.22.03h.25c1.68.02 3.82.48 5.12 1.73 1.69 1.58 1.53 3.69 1.53 5.98v5.41c0 1.63.67 2.35 1.3 3.22.23.32.28.7 0 .93-.71.6-1.98 1.69-2.67 2.3zm-3.5-8.48c0 1.36.03 2.49-.65 3.7-.55.97-1.43 1.57-2.4 1.57-1.34 0-2.12-1.02-2.12-2.52 0-2.96 2.66-3.5 5.17-3.5v.76z" fill="#333E47"/></g></svg>
            {/if}
        </button>
    </div>
{/if}
{#if errorMessage}
    <p class="text-scarlet text-sm mt-1">{errorMessage}</p>
{/if}
